<template>
  <PForm
    ref="pForm"
    :heading="computedHeading"
    :primary-cta="submitBtn"
    :secondary-cta="resetBtn"
    :ternary-cta="cancelButton"
    :show-footer="formState.view"
    @close="onClose"
    @submit.prevent="showConfirmationDialog"
    @reset="onReset"
  >
    <template v-if="formState.preview || formState.view" #header-info>
      <span v-if="formState.view">
        <PBtn class="q-pa-none" flat dense @click="openFilesDrawer">
          <DocumentIcon color="primary" class="icon--sm q-mr-xxs" />
          <span class="text-xs">
            Files ({{ formData?.bill_attached_files?.length || 0 }})
          </span>
        </PBtn>
      </span>
      <span>
        <PBadge
          :type="getStatusLabel(formData)"
          :label="getStatusLabel(formData)"
          class="text-capitalize q-mx-md"
        />
        <PTooltip
          v-if="formData.status === 'Cancelled' && formData?.reason?.length"
        >
          {{ formData.reason }}
        </PTooltip>
      </span>
      <PBtn
        v-if="formState.preview && formData?.ai_verification?.reason"
        :fluid="false"
        class="q-mr-xl"
        ai
      >
        <AiStarsIcon class="icon--sm q-mr-xxs" />
        <span>AI Suggest</span>
        <PMenu anchor="bottom left" self="top left">
          <div
            class="q-pa-2x-md"
            v-html="formData?.ai_verification?.reason"
          ></div>
        </PMenu>
      </PBtn>
    </template>
    <template v-if="formState.view" #sub-heading>
      <span class="text-xs text-neutral-70">
        Balance Due:
        <span class="text-neutral">
          {{ getDueBalance(formData) }}
        </span>
      </span>
    </template>
    <div
      v-if="computedViewPurchaseBill"
      class="border-none q-ma-none q-pa-none"
    >
      <div class="row q-col-gutter-lg">
        <div class="col-6">
          <PSelectInput
            v-model="computedPurchaseOrders"
            :emit-value="false"
            :map-options="false"
            :disable="formState.preview"
            :options="unref(purchaseOrderFilter.entities)"
            :options-loading="unref(purchaseOrderFilter.loading)"
            :option-label="(option) => option.number || option.internal_number"
            help="Optional"
            label="Link to multiple other Purchase Orders"
            option-disable="disable"
            option-value="_id"
            placeholder="Select POs"
            multiple
            use-chips
            virtual-scroll-slice-size="25"
            virtual-scroll-item-size="53"
            @virtual-scroll="purchaseOrderFilter.onScroll"
            @filter="purchaseOrderFilter.onFilter"
            @filter-abort="purchaseOrderFilter.onFilterAbort"
            @update:model-value="populateLineItems"
          >
            <template #option="{ itemProps, opt }">
              <q-item
                v-if="unref(purchaseOrder)._id != opt._id"
                v-bind="itemProps"
              >
                <q-item-section>
                  <q-item-label class="text-neutral text-sm">
                    {{ opt.number || opt.internal_number }}
                  </q-item-label>
                  <q-item-label caption lines="1">
                    Status: {{ opt.status }}
                  </q-item-label>
                </q-item-section>
              </q-item>
            </template>
          </PSelectInput>
        </div>
      </div>
    </div>
    <q-separator v-if="computedViewPurchaseBill" spaced="xl" />
    <UserDetails
      v-if="formState?.view"
      :user-details="purchaseBill"
      source="purchases"
      :module="'pb'"
    />
    <fieldset v-else class="border-none q-ma-none q-pa-none">
      <legend
        v-if="passedProps.source != 'purchases'"
        class="q-mb-sm text-neutral text-weight-medium text-md"
      >
        Vendor Information
      </legend>
      <div class="row q-col-gutter-xl">
        <div class="col-12">
          <PSelectInput
            v-model="formData.seller"
            :emit-value="false"
            :map-options="false"
            :options="unref(sellerFilter.entities)"
            :options-loading="unref(sellerFilter.loading)"
            :option-label="getVendorLabel"
            option-value="_id"
            label="Vendor"
            placeholder="Select Vendor"
            required
            :disable="formState.preview"
            virtual-scroll-slice-size="25"
            virtual-scroll-item-size="53"
            @virtual-scroll="sellerFilter.onScroll"
            @filter="sellerFilter.onFilter"
            @filter-abort="sellerFilter.onFilterAbort"
            @update:model-value="onUpdateSeller"
          >
            <template #option="{ itemProps, opt }">
              <q-item v-bind="itemProps">
                <q-item-section>
                  <q-item-label class="text-neutral text-sm">
                    {{ opt.shop_name }} | GSTIN: {{ opt.gstin }}
                  </q-item-label>
                  <q-item-label caption lines="1">
                    State: {{ opt.state }}
                  </q-item-label>
                </q-item-section>
              </q-item>
            </template>
          </PSelectInput>
          <div
            v-if="showPreviewEInvoiceApplicabilityMessage"
            class="bg-warning-20 rounded-borders q-pa-sm row items-center"
          >
            <q-icon
              name="sym_r_error"
              size="24px"
              class="q-mr-xs text-warning-70"
            />

            <span class="col text-sm">
              E-invoice is applicable to this purchase bill. Please check the
              IRN and QR code in the attached bill pdf.
            </span>
          </div>
        </div>
        <div v-if="formState.preview" :class="getComputedClasses">
          <PInput
            v-model.trim="formData.internal_number"
            label="POSHN PB No."
            placeholder="Enter value"
            disable
          />
        </div>
        <div :class="getComputedClasses">
          <PInput
            v-model.trim="formData.number"
            :rules="[validateNumber]"
            :loading="validatingNumber"
            label="Purchase Bill No."
            placeholder="Enter value"
            required
            debounce="1000"
            :disable="formState.preview || !formData.seller"
            :hide-bottom-space="!formData.seller"
          />
          <p
            v-if="!formData.seller"
            class="text-error-60 text-xs q-pt-xs q-px-sm q-mb-none text-weight-thin"
            :style="{ lineHeight: 1 }"
          >
            Please select Vendor first
          </p>
        </div>
        <div :class="getComputedClasses">
          <PVDateInput
            v-model="formData.date"
            label="Purchase Bill Date"
            placeholder="DD-MM-YYYY"
            required
            :disable="formState.preview"
            :min-date="computedMinDate"
            :max-date="currentDate"
          />
        </div>
        <div :class="getComputedClasses">
          <PInput
            v-model.trim="formData.vehicle_number"
            label="Vehicle No."
            placeholder="Enter value"
            required
            :disable="formState.preview || hasActiveInvoices"
          />
        </div>
        <div v-if="!formState.preview" class="col-3">
          <PSelectInput
            v-model="formData.trade_type"
            label="Trade Type"
            placeholder="Select type"
            :options="['Smart', 'Non Smart']"
            :disable="formState.preview || hasActiveInvoices"
            required
          />
          <q-checkbox
            v-if="passedProps.source !== 'purchases'"
            :model-value="getIsFreightIncluded()"
            :disable="formState.preview || hasActiveInvoices"
            class="text-neutral"
            label="Freight Included in the rate?"
            size="sm"
            @update:model-value="updateIsFreightIncluded"
          />
        </div>
        <div class="col-6 column q-col-gutter-xxs">
          <PSelectInput
            v-model="unref(formData).billing_address"
            :disable="formState.preview"
            :options="unref(poshnBillingAddressFilter.entities)"
            required
            label="Bill To"
            placeholder="Select an address"
            :option-label="(val) => getAddressOptionLabel(val, val?.gstin)"
            :style="computedBillingAddressStyle"
            @update:model-value="
              (value) =>
                updateAddress(
                  value,
                  'billing_address',
                  value?.gstin,
                  'Shambhu Technology Services Private Limited',
                )
            "
            @filter="poshnBillingAddressFilter.onFilter"
            @filter-abort="poshnBillingAddressFilter.onFilterAbort"
            @virtual-scroll="poshnBillingAddressFilter.onScroll"
          >
            <template #option="{ itemProps, opt }">
              <AddressOption
                :selected="opt._id == formData.billing_address_info?.address_id"
                :passed-props="itemProps"
                :address="opt"
                :gstin="opt.gstin"
              />
            </template>
          </PSelectInput>
          <PInput
            v-model.trim="temp_billing_address"
            :disable="true"
            class="col"
            placeholder="Selected billing address"
            type="textarea"
            autogrow
            required
          />
        </div>
        <div class="col-6 column q-col-gutter-xxs">
          <PSelectInput
            v-model="unref(formData).shipping_address"
            :cta="computedAddNewAddressButton"
            :disable="formState.preview || hasActiveInvoices"
            required
            label="Ship To"
            placeholder="Select an address"
            :options="unref(shippingAddressFilter.entities)"
            :option-label="(val) => getAddressOptionLabel(val)"
            @filter="shippingAddressFilter.onFilter"
            @filter-abort="shippingAddressFilter.onFilterAbort"
            @virtual-scroll="shippingAddressFilter.onScroll"
            @update:model-value="
              (value) =>
                updateAddress(
                  value,
                  'shipping_address',
                  unref(purchaseOrder)?.buyer?.gstin,
                  unref(purchaseOrder)?.buyer?.shop_name,
                )
            "
          >
            <template #option="{ itemProps, opt }">
              <AddressOption
                :selected="
                  opt._id == formData.shipping_address_info?.address_id
                "
                :passed-props="itemProps"
                :address="opt"
                :gstin="unref(purchaseOrder)?.buyer?.gstin"
              />
            </template>
          </PSelectInput>
          <PInput
            v-model.trim="temp_shipping_address"
            :disable="true"
            class="col"
            placeholder="Selected shipping address"
            type="textarea"
            autogrow
            required
          />
        </div>
      </div>
    </fieldset>
    <q-separator class="q-mb-xl q-mt-sm" />
    <div class="overflow-hidden">
      <legend class="q-mb-sm text-neutral text-weight-medium text-md">
        Items
      </legend>

      <q-separator v-if="formData?.lineitems?.length" />

      <PTable
        v-if="formData?.lineitems?.length"
        :pagination="pagination"
        rows-per-page-label=" "
        :rows-per-page-options="[]"
        :columns="productListColumns"
        :rows="formData.lineitems"
        :style="{ margin: '0 -15px', padding: 0 }"
        table-header-class="text-neutral-70 bg-neutral-10"
        dense
        flat
        wrap-cells
      >
        <template #body-cell="props">
          <q-td :key="props.col.name" :props="props">
            <PEditable v-model="props.row[props.col.name]" disable />
          </q-td>
        </template>

        <template #body-cell-name="props">
          <q-td :key="props.col.name" :props="props">
            <ItemNameCell :col="props.col" :table-props="props" />
          </q-td>
        </template>

        <template #body-cell-qty="props">
          <q-td :key="props.col.name" :props="props">
            <ItemQuantityCell :col="props.col" :table-props="props" />
          </q-td>
        </template>

        <template #body-cell-rate="props">
          <q-td :key="props.col.name" :props="props">
            <ItemRateCell :col="props.col" :table-props="props" />
          </q-td>
        </template>

        <template #body-cell-amount="props">
          <q-td :key="props.col.name" :props="props">
            <ItemAmountCell :col="props.col" :table-props="props" />
          </q-td>
        </template>

        <template #body-cell-po_qty="props">
          <q-td :key="props.col.name" :props="props">
            <div class="align-middle">
              <p class="q-mb-none">{{ roundOff(props.value) }}</p>
              <p class="q-mb-none">{{ props.row.po_unit }}</p>
            </div>
          </q-td>
        </template>

        <template #body-cell-conversion_factor="props">
          <q-td :key="props.col.name" :props="props">
            <PEditable
              v-if="!formState.preview"
              v-model.number="props.row[props.col.name]"
              class="full-width"
              type="number"
              disable
            />
            <span
              v-else-if="props.row.unit === props.row.po_unit"
              class="ellipsis text-neutral-90 text-xs"
            >
              Same Units
            </span>
            <span v-else class="ellipsis text-neutral-90 text-xs">
              {{ props.row.is_po_unit_bigger ? props.value : 1 }}
              {{ props.row.unit || "Unit" }}
              =
              {{ props.row.is_po_unit_bigger ? 1 : props.value }}
              {{ props.row.po_unit || "Unit" }}
            </span>
          </q-td>
        </template>

        <template #body-cell-is_po_unit_bigger="props">
          <q-td :props="props">
            <CheckMarkIcon v-if="props.value" class="icon--sm text-primary" />
            <CloseIcon v-else class="icon--sm text-primary" />
          </q-td>
        </template>

        <template #body-cell-po_rate="props">
          <q-td :key="props.col.name" :props="props">
            <span class="ellipsis text-neutral text-xs">
              {{ props.value }}
            </span>
          </q-td>
        </template>

        <template #body-cell-action="props">
          <q-td :props="props">
            <div class="flex no-wrap justify-center">
              <PBtn
                :disable="formState.preview || props.row.disable"
                color="primary"
                dense
                flat
                fluid
                rounded
                title="Edit Item"
                @click="onEditItem(props.row, props.rowIndex)"
              >
                <EditIcon class="icon--sm" />
              </PBtn>
              <PBtn
                :disable="formState.preview || props.row.disable"
                dense
                flat
                rounded
                title="Delete Item"
                @click="onDeleteLineItem(props.row, props.rowIndex)"
              >
                <RemoveIcon class="icon--sm" />
              </PBtn>
            </div>
          </q-td>
        </template>
      </PTable>
      <p
        v-else-if="passedProps.source === 'purchases'"
        class="text-neutral-50 q-mb-none"
      >
        <span class="block q-mb-xs">No Items Added</span>
        <span class="text-error-60">
          <q-icon class="icon--sm" name="sym_r_error_outline" />
          At least one item is required.
        </span>
      </p>
      <span v-else class="block text-negative">
        At least 1 Product is required
      </span>
      <span
        v-if="hasItemsWithZeroQtyOrRate"
        class="block q-mt-sm text-error-60"
      >
        <q-icon class="icon--sm" name="sym_r_error_outline" />
        Every item should have qty, rate and total greater than 0.
      </span>
      <PBtn
        v-if="showAddFreightBtn"
        :fluid="false"
        :loading="isFetchingFreightItems"
        class="q-mt-md"
        dense
        outline
        @click="onAddFreight"
      >
        <AddIcon class="icon--sm q-mr-xxs" />
        <span>Add Freight</span>
      </PBtn>
      <span
        v-if="formData?.is_freight_included && formState?.view"
        class="text-xxs text-neutral text-italic"
      >
        <sup>*</sup>Freight is included in the rate
      </span>
      <PBtn
        v-if="passedProps.source === 'purchases'"
        :disable="formData.rfq != null"
        class="q-mt-md"
        outline
        @click="
          addItem({
            entityType: 'Purchase Bill',
            source: unref(passedProps).source,
          })
        "
      >
        <AddIcon class="icon--sm q-mr-sm" />
        <span>Add Line Item</span>
      </PBtn>
    </div>
    <q-separator spaced="xl" />
    <div class="border-none q-ma-none q-pa-none">
      <div class="row q-col-gutter-lg">
        <div v-if="formState.view" class="col-6">
          <div>
            <span class="text-neutral-80">Remarks </span>
          </div>
          <div>
            <span> {{ purchaseBill?.remarks || "" }} </span>
          </div>
        </div>
        <div v-else class="col-6">
          <PInput
            v-model.trim="formData.remarks"
            label="Remarks (if any)"
            placeholder="Enter remarks"
            type="textarea"
            autogrow
            :disable="formState.preview || hasActiveInvoices"
          />
        </div>
        <div class="col-6">
          <div class="row q-col-gutter-xs">
            <div class="col-6">
              <p class="q-mb-none">Total Amount</p>
              <span class="text-xs text-neutral-70"> Excludes Tax </span>
            </div>
            <div class="col-6">
              <div class="text-right">
                {{
                  numberFormat({
                    number: getTotalAmountWithoutTax() ?? 0,
                    style: "currency",
                  })
                }}
              </div>
            </div>
            <div class="col-6">
              <p class="q-mb-none">Tax Total</p>
              <span class="text-xs text-neutral-70"> GST + CESS </span>
            </div>
            <div class="col-6">
              <div class="text-right">
                {{
                  numberFormat({
                    number: formData.total_tax,
                    style: "currency",
                  })
                }}
              </div>
            </div>
            <div v-if="formData.tds?.code && !formState?.view" class="col-6">
              <TdsInfoTooltip :percentage="formData.tds?.percentage" />
            </div>
            <div v-if="formData.tds?.code && !formState?.view" class="col-6">
              <div class="text-right">
                -
                {{
                  numberFormat({
                    number: formData.tds?.amount,
                    style: "currency",
                  })
                }}
              </div>
            </div>
            <div class="col-12">
              <q-separator />
            </div>

            <ServiceItemList
              v-if="isVasApplicableOnSeller"
              :vas-items="formData.vas_items"
              :view-mode="formState.view || hasActiveInvoices"
              class="col-12"
              @add="addServiceItem"
              @remove="removeVasItem"
            />

            <div class="col-6">
              <p class="q-mb-none">Grand Total</p>
              <span class="text-xs text-neutral-70"> Includes Tax </span>
            </div>
            <div class="col-6">
              <div class="text-right">
                {{
                  numberFormat({
                    number: formData.total_amount,
                    style: "currency",
                  })
                }}
              </div>
            </div>
            <div v-if="formState?.view" class="row q-col-gutter-xs">
              <div v-if="formData.tds?.code" class="col-6 q-mb-xs">
                <TdsInfoTooltip :percentage="formData.tds?.percentage" />
              </div>
              <div v-if="formData.tds?.code" class="col-6">
                <div class="text-right">
                  {{ formData.tds?.amount ? "-" : "" }}
                  {{
                    numberFormat({
                      number: formData.tds?.amount ?? 0,
                      style: "currency",
                    })
                  }}
                </div>
              </div>
              <div class="col-6">
                <p>Applied Credits</p>
              </div>
              <div class="col-6">
                <div class="text-right">
                  {{
                    formData?.payment_summary?.total_applied_credits ? "-" : ""
                  }}
                  {{
                    numberFormat({
                      number:
                        formData?.payment_summary?.total_applied_credits ?? 0,
                      style: "currency",
                    })
                  }}
                </div>
              </div>
              <div class="col-6">
                <p>Payments Made</p>
              </div>
              <div class="col-6">
                <div class="text-right">
                  {{ formData?.payment_summary?.total_paid_amount ? "-" : "" }}
                  {{
                    numberFormat({
                      number: formData?.payment_summary?.total_paid_amount ?? 0,
                      style: "currency",
                    })
                  }}
                </div>
              </div>
              <div class="col-6">
                <p>Balance Due</p>
              </div>
              <div class="col-6">
                <div class="text-right">
                  {{
                    numberFormat({
                      number: formData.payment_summary?.balance_due ?? 0,
                      style: "currency",
                    })
                  }}
                </div>
              </div>
            </div>
          </div>
          <div
            v-if="formState.preview && formData.seller?.tax_settings"
            class="row q-mt-xs"
          >
            <div class="col-6">
              <p class="q-mb-none">Net Payable</p>
              <span class="text-xs text-neutral-70"> Check DN Separately </span>
            </div>
            <div class="col-6">
              <div class="text-right">
                {{
                  numberFormat({
                    number: formData.total_amount - (formData.tds?.amount ?? 0),
                    style: "currency",
                  })
                }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="!formState.preview" class="row q-col-gutter-lg">
      <div class="col-12">
        <PFile
          v-if="!formState.edit"
          v-model="file"
          :label="`Attach Proof of Document (Purchase Bill)`"
          help="Allowed file types are PDFs, images, excel, csv"
        />
        <PFile
          v-if="file"
          v-model="file"
          class="q-mt-xxs"
          variant="re-upload"
        />
        <div
          v-if="showInfoBanner"
          class="flex item-center bg-info-10 text-info-60 q-pa-xxs q-mt-lg"
        >
          <InfoIcon class="icon q-mr-xs" />
          <span> Only add stock/inventory type purchase bills </span>
        </div>
        <FilesList
          v-if="formState.edit"
          :entity="formData"
          :removal-allowed="false"
          file-type="Purchase Bill"
        />
      </div>
    </div>
    <q-separator class="q-mb-xl q-mt-md" />
    <div v-if="formState?.view">
      <AssociatedVouchers :id="formData?._id" :type="'pb'" />
    </div>
    <div v-else-if="formState.preview">
      <p>Associated Debit Notes ({{ debitNotes.length }})</p>

      <q-separator v-if="debitNotes?.length" />

      <PTable
        v-if="debitNotes.length"
        :columns="debitNoteColumns"
        :rows="debitNotes"
        expandable-rows
        hide-bottom
        dense
      >
        <template #row="props">
          <q-td v-for="col in props.cols" :key="col.name" :props="props">
            <span v-if="col.name === 'number'">
              <PBtn
                class="text-xs"
                dense
                flat
                fluid
                text
                @click="previewDebitNote(props.row)"
              >
                {{ col.value || "View Details" }}
              </PBtn>
            </span>

            <PBadge
              v-else-if="col.name === 'stage'"
              :label="col.value"
              :type="col.value"
            />

            <PPopover v-else-if="col.name === 'remarks'" :value="col.value" />

            <span v-else>{{ col.value }}</span>
          </q-td>
        </template>
        <template #expanded-row="props">
          <q-td></q-td>
          <q-td :colspan="productsTableColumns.length - 1">
            <div class="flex">
              <PTabs
                :model-value="getActiveTab(props.rowIndex)"
                align="left"
                dense
                shrink
                @update:model-value="
                  (activeTab) => setActiveTab(activeTab, props.rowIndex)
                "
              >
                <PTab
                  v-for="({ disable, name, label }, index) in tabs"
                  :key="index"
                  :class="{ 'text-neutral-70': activeTab !== name }"
                  :disable="disable"
                  :name="name"
                  :label="label"
                  class="col"
                />
              </PTabs>
            </div>
            <q-tab-panels
              :model-value="getActiveTab(props.rowIndex)"
              class="transparent"
              animated
            >
              <q-tab-panel class="q-pa-none" name="products">
                <PMarkupTable
                  :columns="productsTableColumns"
                  :rows="props.row.lineitems"
                />
              </q-tab-panel>
            </q-tab-panels>
          </q-td>
        </template>
      </PTable>
    </div>
    <section
      v-if="hasActiveInvoices && !formState.preview && !formState.view"
      class="q-my-xxl"
    >
      <p class="text-warning-70">
        There are active invoices against this purchase Bill. Cancel those
        before updating.
      </p>
      <p v-if="activeInvoices.length && !formState.preview && !formState.view">
        Active Invoices : {{ activeInvoices.join(", ") }}
      </p>
    </section>
    <div v-if="computedViewPurchaseBill" class="q-mt-sm text-weight-bold">
      {{ FORM_INFO }}
    </div>
    <div
      v-if="showAddEInvoiceApplicabilityMessage"
      class="bg-warning-20 rounded-borders q-pa-sm row items-center q-mt-xl"
    >
      <q-icon name="sym_r_error" size="24px" class="q-mr-xs text-warning-70" />

      <span class="col text-sm">
        E-invoice is applicable for this vendor. Please check for IRN and QR
        code in the bill.
      </span>
    </div>
  </PForm>
</template>

<script setup>
import { date, useQuasar } from "quasar";

import AddIcon from "assets/icons/add.svg";
import EditIcon from "assets/icons/edit.svg";
import RemoveIcon from "assets/icons/remove.svg";
import CheckMarkIcon from "assets/icons/checkmark.svg";
import CloseIcon from "assets/icons/close.svg";
import DocumentIcon from "assets/icons/document.svg";
import InfoIcon from "assets/icons/info.svg";
import AiStarsIcon from "assets/icons/ai-star-outline.svg";

import purchaseBillFormModel from "src/modules/purchase-order/models/purchaseBillForm";
import PConfirmDialog from "src/components/PConfirmDialog.vue";

import { FORM_INFO } from "assets/scripts/utilities/constants";
import { numberFormat, roundOff } from "assets/scripts/utilities/helpers";

import { index as getAddress } from "src/api/address";
import * as productApi from "src/api/product";
import { notify } from "assets/scripts/utilities/api-clients";
import { unref, watchEffect, watch, computed } from "vue";
import { useRouterUtilities } from "src/composables/routerUtilities";
import { useCancelPurchaseBill } from "../../composables/cancelPurchaseBill";
import { useEInvoiceApplicabilityChecker } from "../../composables/e-invoice-applicability-checker";

const { removeQueryParams } = useRouterUtilities();

const { formatDate } = date;
const $q = useQuasar();

const { viewFile } = useFile();

const passedProps = defineProps({
  toggle: {
    type: Object,
    default: () => {
      return {
        method: false,
        options: {},
      };
    },
  },
  source: {
    type: String,
    default: "",
  },
});

const { toggleAssociatedDataDrawer } = usePaymentRequestStore();
const { getStatusLabel, getDueBalance } = usePaymentStatus();

const pForm = ref(null);
const validatingNumber = ref(false);
const isDuplicatePB = ref(false);
const dummyBuyerDetails = ref({});

const currentDate = new Date();

const computedMinimumDate = computed(
  () => new Date(unref(purchaseOrder)?.date),
);

const getComputedClasses = computed(() => {
  if (unref(formState).preview) {
    return "col-6";
  }

  return "col-3";
});

const purchaseOrderFilter = useFilter({
  entity: "purchaseOrder",
  options: {
    params: {
      status_cd: [
        "p_fulfilled",
        "completed",
        "approved",
        "pod_received",
        "active",
        "partially_fulfilled",
        "in_transit_p",
        "closed",
        "p_closed",
        "p_fullfilled",
      ].join(","),
    },
  },
  props: ["number", "internal_number"],
});

let freightCategoryId = ref(null);

const validateNumber = async (value) => {
  try {
    validatingNumber.value = true;

    isDuplicatePB.value = false;

    const payload = {
      number: value,
      _id: unref(formData)?._id,
      seller: unref(formData).seller,
    };

    await isDuplicate(payload);

    validatingNumber.value = false;

    return true;
  } catch (error) {
    validatingNumber.value = false;

    isDuplicatePB.value = true;

    if (error?.response?.status >= 400 && error?.response?.status < 500) {
      return "Purchase Bill Number Already exists";
    }

    return true;
  }
};

const { purchaseOrder } = storeToRefs(usePurchaseOrderStore());
const { debitNotes } = storeToRefs(useDebitNoteStore());
const { nestedRightDrawers, rightDrawerComponent, rightDrawerComponentProps } =
  storeToRefs(useAppStore());

const { formState, purchaseBill } = storeToRefs(usePurchaseBillStore());
const {
  getPurchaseBills,
  postPurchaseBill,
  putPurchaseBill,
  togglePurchaseBillForm,
  isDuplicate,
} = usePurchaseBillStore();

const { toggleAddAddressForm } = useAddressStore();

const { getUser } = useUserStore();

import { getDummyBuyer } from "src/api/user";
import UserDetails from "src/modules/credit-notes/components/UserDetails.vue";

let poshnBillingAddressFilter = useFilter({
  entity: "address",
  options: {
    params: {
      type: "billing",
      applies_to: "poshn",
    },
  },
});

const initBillToFilter = () => {
  poshnBillingAddressFilter = useFilter({
    entity: "address",
    options: {
      params: {
        type: "billing",
        applies_to: "poshn",
      },
    },
  });
};

const temp_billing_address = ref("");
const temp_shipping_address = ref("");

const getAddressOptionLabel = (addressOption, gstin = "") => {
  if (!addressOption) return;

  if (typeof addressOption === "string") {
    return addressOption;
  }

  const { address_line, address, city, state, pincode } = addressOption;

  let gstinVal = gstin;

  if (!gstinVal) {
    gstinVal =
      unref(passedProps).source === "purchases"
        ? dummyBuyerDetails.value?.gstin
        : unref(purchaseOrder)?.buyer?.gstin;
  }

  const addressParts = [address_line ?? address, city, state, pincode].filter(
    Boolean,
  );

  let label = addressParts.join(", ");

  if (gstinVal) {
    label += `\nGSTIN: ${gstinVal}`;
  }

  return label;
};

const updateAddress = (info, address_type, gstin, shop_name) => {
  if (!info) {
    return;
  }

  const { _id, city, state, pincode, address_line, address, state_code } = info;

  unref(formData)[`${address_type}_info`] = {
    address_id: _id,
    address_line: address_line ?? address,
    city,
    pincode,
    shop_name,
    state,
    state_code,
  };

  const addressLine = address_line ?? address;

  const gstinVal =
    unref(passedProps).source === "purchases"
      ? dummyBuyerDetails.value?.gstin
      : gstin;

  const shopName =
    unref(passedProps).source === "purchases"
      ? dummyBuyerDetails.value?.shop_name
      : shop_name;

  const gstinLabel = gstinVal ? `\nGSTIN: ${gstinVal}` : "";

  const concatenatedAddress = `${addressLine}${
    city ? ", " + city : ""
  }${state ? ", " + state : ""}${pincode ? ", " + pincode : ""}${gstinLabel}`;

  unref(formData)[`${address_type}`] = concatenatedAddress;

  if (address_type === "billing_address") {
    temp_billing_address.value = `${shopName}\n${concatenatedAddress}`;
  } else if (address_type === "shipping_address") {
    temp_shipping_address.value = `${shopName}\n${concatenatedAddress}`;
  }
};

const {
  lineitems = [],
  shipping_address,
  shipping_address_info,
} = unref(purchaseOrder);

let shippingAddressFilter = ref(null);

watch(
  [() => unref(dummyBuyerDetails)?._id, () => unref(purchaseOrder)?.buyer?._id],
  () => {
    shippingAddressFilter.value = useFilter({
      entity: "address",
      options: {
        params: {
          get_primary: unref(passedProps).source === "purchases" ? null : true,
          type: "shipping",
          user_id:
            unref(passedProps).source === "purchases"
              ? unref(dummyBuyerDetails)?._id
              : unref(purchaseOrder)?.buyer?._id,
        },
      },
    });
  },
  { immediate: true },
);

const initShipToFilter = () => {
  shippingAddressFilter.value = useFilter({
    entity: "address",
    options: {
      params: {
        get_primary: unref(passedProps).source === "purchases" ? null : true,
        type: "shipping",
        user_id:
          unref(passedProps).source === "purchases"
            ? unref(dummyBuyerDetails)?._id
            : unref(purchaseOrder)?.buyer?._id,
      },
    },
  });
};

const computedPurchaseOrders = computed({
  get: () => {
    return unref(formData)?.po_id?.filter((po) => {
      const poId = po._id ? po._id : po;

      return poId !== unref(purchaseOrder)?._id;
    });
  },
  set: (linkedPOs) => {
    linkedPOs.unshift(unref(purchaseOrder));

    unref(formData).po_id = linkedPOs;
  },
});

const populateLineItemData = (lineitem) => {
  const { lineitems } = purchaseBillFormModel()();
  const { amount, qty, rate, unit } = lineitem;

  return {
    ...lineitems[0],
    ...lineitem,
    po_amount: Number(amount),
    po_qty: Number(qty),
    po_rate: Number(rate),
    po_unit: unit,
    qty: "",
    rate: undefined,
    amount: "",
    rate_wt: undefined,
    amount_wt: "",
  };
};

const computedViewPurchaseBill = computed(() => {
  return (
    !unref(formState).preview &&
    !unref(formState).view &&
    unref(passedProps).source !== "purchases"
  );
});

const computedMinDate = computed(() => {
  return !hasActiveInvoices.value && unref(passedProps).source !== "purchases"
    ? computedMinimumDate.value
    : null;
});

const computedBillingAddressStyle = computed(() => {
  return unref(passedProps).source === "purchases" && !unref(formState).edit
    ? { marginTop: "4px" }
    : {};
});

const computedAddNewAddressButton = computed(() => {
  return unref(purchaseOrder)?.buyer ||
    unref(passedProps).source === "purchases"
    ? addNewAddressButton.value
    : null;
});

const showAddFreightBtn = computed(() => {
  return !unref(formData).is_freight_included && computedViewPurchaseBill.value;
});

const showInfoBanner = computed(() => {
  return (
    unref(passedProps).source === "purchases" &&
    !unref(formState).view &&
    !unref(formState).preview &&
    file.value
  );
});

const computedHeading = computed(() => {
  const state = unref(formState);
  const base = unref(formData).number;

  if (state?.view) {
    return base;
  }

  if (state.preview) return "View Purchase Bill";

  if (state.edit) return "Edit Purchase Bill";

  return "Add Purchase Bill";
});

const populateLineItems = () => {
  const linkedPOs = unref(computedPurchaseOrders)?.filter((po) => !po.disable);

  const linkedLineItems = linkedPOs.reduce((acc, po) => {
    return [...acc, ...po.lineitems];
  }, []);

  let poLineItemIds = lineitems?.map(
    (lineItem) => lineItem._id || lineItem.item_id,
  );

  const lineItems = unref(formState).edit
    ? unref(purchaseBill).lineitems
    : unref(formData).lineitems?.filter((lineItem) =>
        poLineItemIds.includes(lineItem._id || lineItem.item_id),
      );

  unref(formData).lineitems = [
    ...lineItems,
    ...linkedLineItems.map(populateLineItemData),
  ];

  let freightItem = null;

  const freightItemIndex = unref(formData).lineitems.findIndex((lineItem) =>
    isFreightItem(lineItem),
  );

  if (freightItemIndex !== -1) {
    freightItem = unref(formData).lineitems.splice(freightItemIndex, 1)[0];

    unref(formData).lineitems.push(freightItem);
  }
};

const { formData, isLoading, closeForm, resetForm, submitForm, validateForm } =
  useForm({
    formModel: purchaseBillFormModel({
      lineitems: unref(formState).edit
        ? []
        : lineitems?.map(populateLineItemData),
      po_id: [unref(purchaseOrder)],
      shipping_address_info,
      shipping_address,
      total_amount: 0,
      total_tax: 0,
    }),
    entity: purchaseBill,
    formRef: pForm,
    isForm: formState,
    post: () =>
      postPurchaseBill(unref(formData), {
        params: {
          is_stock: unref(passedProps).source === "purchases" ? true : null,
          showMessage: unref(passedProps).source === "purchases" ? null : false,
        },
      }),
    put: putPurchaseBill,
    toggleForm: togglePurchaseBillForm,
  });

const getPoshnBillingAddress = async (stateCode) => {
  if (!stateCode) {
    return null;
  }

  const response = await getAddress({
    params: {
      type: "billing",
      applies_to: "poshn",
      state_code: stateCode,
      page_size: 1,
    },
  });

  return response?.data?.data?.data?.[0] || null;
};

const setBillingAddress = async () => {
  const sellerPrimaryAddress = await getAddress({
    params: {
      get_primary: true,
      user_id: unref(formData).seller._id,
    },
  });

  const sellerPrimaryAddressStateCode =
    sellerPrimaryAddress?.data?.data?.data?.[0]?.state_code;

  // The state match happens on the server, so this no longer depends on every
  // Poshn billing address fitting in one page.
  const billingAddress =
    (await getPoshnBillingAddress(sellerPrimaryAddressStateCode)) ||
    (await getPoshnBillingAddress("DL"));

  updateAddress(
    billingAddress,
    "billing_address",
    billingAddress?.gstin,
    "Shambhu Technology Services Private Limited",
  );
};

const isVasApplicableOnSeller = computed(() => {
  return unref(formData).seller?.is_vas_applicable;
});

const { addVasItem, populateVasItems, removeVasItem } = useVASItems({
  formData,
});

const addServiceItem = () => {
  addVasItem({ entityType: "Purchase Bill", categories: ["VAS"] });
};

const onUpdateSeller = async (seller) => {
  if (unref(formState).edit) {
    validateForm();
  } else {
    unref(formData).number = "";
  }

  calculateTds();

  initBillToFilter();

  initShipToFilter();

  await setBillingAddress();

  if (seller?.is_vas_applicable) {
    populateVasItems();
  }
};

const sellerFilter = useFilter({
  entity: "user",
  props: ["gstin", "shop_name"],
  options: {
    params: {
      roles: "seller",
      isActive: 1,
      isOperational: 1,
    },
  },
});

const hasActiveInvoices = computed(() => {
  return unref(formData).invoices?.length > 0;
});

const {
  showAddEInvoiceApplicabilityMessage,
  showPreviewEInvoiceApplicabilityMessage,
} = useEInvoiceApplicabilityChecker({
  formData,
  formState,
});

const activeInvoices = computed(() => {
  return (
    unref(formData)?.invoices?.map((invoice) => {
      return invoice.number || invoice._id;
    }) || []
  );
});

const activeInvoiceLineItems = computed(() => {
  return unref(formData).invoices?.reduce((acc, invoice) => {
    return [...acc, ...invoice.lineitems];
  }, []);
});

const isFreightItem = (lineItem) => {
  return (
    lineItem.category_id === unref(freightCategoryId) ||
    lineItem.name.toLowerCase().includes("freight")
  );
};

const status = useColor();

const filesColumns = computed(() => {
  return [
    {
      align: "left",
      label: "Date",
      field: "created_date",
      format: (value) => {
        return formatDate(value, "DD MMM YYYY");
      },
    },
    {
      align: "left",
      label: "Name",
      field: "file_name",
      cellClass: "text-primary text-weight-medium underline cursor-pointer",
      onClick: viewFile,
    },
    {
      align: "center",
      label: "Status",
      field: "status",
      format: (value) =>
        `<span class="bg-${status(value).color} text-${
          status(value).textColor
        } q-px-sm q-py-xxs rounded" title="Status">${value}</span>`,
      cellClass: "q-px-sm q-py-xxs rounded",
    },
  ];
});

const openFilesDrawer = () => {
  toggleAssociatedDataDrawer({
    edit: false,
    level: 0,
    open: true,
    view: true,
    props: {
      columns: unref(filesColumns),
      rows: purchaseBill.value?.bill_attached_files,
      heading: "Associated Files",
      type: "file",
    },
  });
};

watch(
  () => unref(formData).lineitems,
  (lineitems) => {
    const isFreightIncluded = lineitems?.some((items) => isFreightItem(items));

    formData.value.is_freight_included = !isFreightIncluded;
  },
  { deep: true },
);

watchEffect(() => {
  unref(purchaseBill)?.po_id?.forEach((po) => {
    if (
      unref(activeInvoiceLineItems)?.some((lineItem) =>
        po.lineitems.some(
          (li) =>
            li.item_id === lineItem.item_id && li.rate === lineItem.po_rate,
        ),
      )
    ) {
      po.disable = true;
    }
  });
});

watchEffect(() => {
  unref(purchaseOrderFilter.entities)?.forEach((po) => {
    if (
      unref(purchaseBill)
        ?.po_id?.map((po) => po?._id || po)
        ?.includes(po._id)
    ) {
      if (
        unref(activeInvoiceLineItems)?.some((lineItem) =>
          po.lineitems?.some(
            (li) =>
              li.item_id === lineItem.item_id && li.rate === lineItem.po_rate,
          ),
        )
      ) {
        po.disable = true;
      }
    }
  });
});

watchEffect(() => {
  unref(formData).lineitems?.forEach((lineItem) => {
    if (
      unref(activeInvoiceLineItems)?.some(
        (li) => li.item_id === lineItem.item_id && li.pb_rate === lineItem.rate,
      )
    ) {
      lineItem.disable = true;
    }

    if (unref(hasActiveInvoices) && isFreightItem(lineItem)) {
      lineItem.disable = true;
    }
  });
});

const getVendorLabel = (option) => {
  return `${option.shop_name} | GSTIN: ${option.gstin}`;
};

const removeFreightItems = () => {
  unref(formData).lineitems = unref(formData).lineitems?.filter(
    (lineItem) => !isFreightItem(lineItem),
  );

  calculateTotalTax();
};

const getIsFreightIncluded = () => {
  return JSON.parse(unref(formData).is_freight_included);
};

const updateIsFreightIncluded = (isFreightIncluded) => {
  formData.value.is_freight_included = isFreightIncluded;

  if (isFreightIncluded) {
    removeFreightItems();
  }
};

const onDeleteLineItem = (lineitem, lineitemIndex) => {
  removeProduct(lineitemIndex);

  if (isFreightItem(lineitem)) {
    updateIsFreightIncluded(true);
  }
};

const freightItems = ref([]);
const isFetchingFreightItems = ref(false);

const fetchFreightItems = async () => {
  try {
    isFetchingFreightItems.value = true;

    const response = await productApi.index({
      params: {
        isActive: 1,
        search: "freight",
      },
    });

    freightItems.value = response?.data?.data?.data || [];
  } catch (error) {
    console.error(error);
  } finally {
    isFetchingFreightItems.value = false;
  }
};

const onAddFreight = async () => {
  await fetchFreightItems();

  addItem({
    entityType: "Purchase Bill",
    items: unref(freightItems),
    source: unref(passedProps).source,
  });
};

const onEditItem = async (item, index) => {
  let items = [];

  if (isFreightItem(item)) {
    await fetchFreightItems();

    items = unref(freightItems);
  }

  editItem({
    item,
    index,
    entityType: "Purchase Bill",
    items,
    source: unref(passedProps).source,
  });
};

const {
  calculateTotalTax,
  addItem,
  editItem,
  hasItemsWithZeroQtyOrRate,
  pagination,
  removeProduct,
  getTotalAmountWithoutTax,
  calculateTds,
} = useProductList({
  formData,
});

const productListColumns = computed(() => {
  let columns = [
    {
      align: "left",
      name: "name",
      label: unref(formState)?.view ? "Product Name" : "Name*",
      field: "name",
    },
    {
      align: "left",
      name: "qty",
      label: unref(formState)?.view ? "Qty" : "Qty*",
      field: "qty",
    },
    {
      align: "right",
      name: "rate",
      label: "Rate",
      field: "rate",
      type: "currency",
    },
    {
      align: "right",
      name: "amount",
      label: "Total",
      field: "amount",
      type: "currency",
    },
    {
      align: "left",
      name: "po_qty",
      label: "PO Qty",
      field: "po_qty",
      format: (value) => value || "-",
      hidden:
        unref(formState)?.view || unref(passedProps).source === "purchases",
    },
    {
      align: "left",
      name: "conversion_factor",
      label: "Conv. Factor",
      field: "conversion_factor",
      hidden:
        unref(formState)?.view || unref(passedProps).source === "purchases",
    },
    {
      align: "center",
      name: "is_po_unit_bigger",
      label: "PO Unit Big?",
      field: "is_po_unit_bigger",
      hidden:
        unref(formState)?.view || unref(passedProps).source === "purchases",
    },
    {
      align: "right",
      name: "po_rate",
      label: "PO Rate (Excl. Tax)",
      field: "po_rate",
      format: (value) =>
        value ? numberFormat({ number: value, style: "currency" }) : "-",
      disable: true,
      hidden:
        unref(formState)?.view || unref(passedProps).source === "purchases",
    },
    {
      align: "center",
      classes: "sticky--right",
      headerClasses: "sticky--right",
      name: "action",
      label: "Action",
      hidden: unref(formState)?.view,
    },
  ];

  const hiddenInViewMode = new Set([
    "is_tax_included",
    "po_unit",
    "is_po_unit_bigger",
    "po_rate",
    "po_qty",
  ]);

  if (unref(formState).preview || unref(formState).view) {
    return columns?.filter((column) => {
      return !hiddenInViewMode.has(column.name);
    });
  }

  return columns;
});

const debitNoteColumns = ref([
  {
    align: "left",
    name: "number",
    label: "Number",
    field: "number",
  },
  {
    align: "left",
    name: "date",
    label: "Date",
    field: "date",
    format: (date, dn) =>
      dn.stage === "Completed" ? formatDate(date, "DD MMM YYYY") || "-" : "-",
  },
  {
    align: "left",
    name: "stage",
    label: "Stage",
    field: "stage",
  },
  {
    align: "right",
    name: "total_amount",
    label: "Total",
    field: "total_amount",
    format: (totalAmount) => {
      return numberFormat({
        number: totalAmount,
        style: "currency",
      });
    },
  },
]);

const tabs = ref([
  {
    disable: false,
    name: "products",
    label: "Products",
  },
]);

const activeTab = ref("products");

const { setActiveTab, getActiveTab } = useTabs(activeTab);

const productsTableColumns = ref([
  {
    label: "No.",
    field: "rowIndex",
    format: (value) => {
      return value + 1;
    },
  },
  {
    label: "Name",
    field: "name",
  },
  {
    label: "HSN/SAC",
    field: "hsn",
  },
  {
    label: "GST (%)",
    field: "gst",
  },
  {
    label: "CESS (%)",
    field: "cess",
  },
  {
    label: "Qty",
    field: "qty",
    format: (value, row) => `${value} ${row.unit || "Unit"}`,
  },
  {
    align: "right",
    label: "Rate (Excl. Tax)",
    field: "rate",
    format: (value) => numberFormat({ number: value, style: "currency" }),
  },
  {
    align: "right",
    label: "Rate (Incl. Tax)",
    field: "rate_wt",
    format: (value) =>
      value ? numberFormat({ number: value, style: "currency" }) : "-",
  },
  {
    align: "right",
    label: "Amount (Excl. Tax)",
    field: "amount",
    format: (value) => numberFormat({ number: value, style: "currency" }),
  },
  {
    align: "right",
    label: "Amount (Incl. Tax)",
    field: "amount_wt",
    format: (value) =>
      value ? numberFormat({ number: value, style: "currency" }) : "-",
  },
]);

const toggleMethod = () => {
  if (passedProps.toggle.method) {
    passedProps.toggle.method(passedProps.toggle.options);
  }
};

const { getCounts, getPurchaseOrderFiles } = usePurchaseOrderStore();
const { file, uploadFile } = useFile(formData);

const onSubmit = async () => {
  try {
    if (!unref(formData).lineitems?.length) {
      return;
    }

    const submission = await submitForm(false);

    isLoading.value = true;

    if (unref(file)) {
      await uploadFile({
        ...submission?.data?.data,
        type: "Purchase Bill",
        showMessage: false,
      });
    }

    if (unref(purchaseOrder)?._id) {
      await Promise.all([
        getCounts(unref(purchaseOrder)?._id),
        getPurchaseOrderFiles(),
        getPurchaseBills(),
      ]);
    } else {
      await getPurchaseBills();
    }

    isLoading.value = false;

    closeForm(true);

    resetAddressRefs();

    if (passedProps.source === "purchases" && submission?.data?.message) {
      notify({
        type: submission?.data?.status,
        message: submission?.data?.message,
      });
    }

    submission?.data?.messages?.forEach((info) => {
      notify({
        type: info.type,
        message: info.msg,
      });
    });
  } catch (error) {
    isLoading.value = false;

    if (error?.data?.message) {
      notify({
        type: "error",
        message: error.data.msg,
      });
    }
  }
};

const showConfirmationDialog = () => {
  if (unref(passedProps).source === "purchases") {
    $q.dialog({
      component: PConfirmDialog,
      componentProps: {
        title: "Submit Purchase Bill",
        text: "Added Stock Items Only?",
        label:
          "Please make sure you are adding only items for stocking to this Purchase Bill. Do you want to continue?",
        isFooterVisible: true,
        labelClass: "bg-none",
        cardStyle: { width: "550px" },
        submittable: true,
        cancellable: true,
        closable: true,
        onConfirm: onSubmit,
      },
    });
  } else {
    onSubmit();
  }
};

const addNewAddress = (addressType) => {
  toggleAddAddressForm({
    open: true,
    level: 0,
    props: {
      documentType: "Purchase Bill",
      userId:
        passedProps.source === "purchases"
          ? dummyBuyerDetails.value?._id
          : unref(purchaseOrder)?.buyer._id,
      addressType,
      updateParent: selectAddress,
    },
  });
};

const selectAddress = (newAddressData) => {
  if (!newAddressData) {
    return;
  }

  updateAddress(
    newAddressData,
    "shipping_address",
    passedProps.source === "purchases"
      ? dummyBuyerDetails.value?.gstin
      : unref(purchaseOrder)?.buyer?.gstin,
    passedProps.source === "purchases"
      ? dummyBuyerDetails.value?.shop_name
      : unref(purchaseOrder)?.buyer?.shop_name,
  );
};

const addNewAddressButton = computed(() => {
  if (unref(formState).preview || unref(formState).view) {
    return {};
  }

  return {
    dense: true,
    flat: true,
    fluid: true,
    text: true,
    icon: "sym_r_add",
    label: "Add New Address",
    disable: unref(hasActiveInvoices),
    onClick: () => addNewAddress("shipping"),
  };
});

const submitBtn = computed(() => {
  if (unref(formState).preview || unref(formState).view) {
    return {};
  }

  return {
    loading: unref(isLoading),
    disable:
      !unref(formData).lineitems?.length ||
      !unref(formData).total_amount ||
      unref(validatingNumber) ||
      unref(isDuplicatePB) ||
      unref(hasItemsWithZeroQtyOrRate),
  };
});

const resetBtn = computed(() => {
  if (!unref(formState).preview && !unref(formState).view) {
    return {
      visible: true,
    };
  }

  return {};
});

const { setDebitNote, toggleDebitNoteForm } = useDebitNoteStore();

const previewDebitNote = (debitNote) => {
  setDebitNote(debitNote);

  toggleDebitNoteForm({
    level: 1,
    edit: true,
    open: true,
    preview: true,
  });
};

const resetAddressRefs = () => {
  if (unref(formState).edit) {
    temp_billing_address.value = `${unref(purchaseBill).billing_address_info?.shop_name ?? ""}\n${unref(purchaseBill).billing_address}`;

    temp_shipping_address.value = `${unref(purchaseBill).shipping_address_info?.shop_name ?? ""}\n${unref(purchaseBill).shipping_address}`;
  } else {
    temp_billing_address.value = "";

    // get shipping address from purchase order
    let shop_name = shipping_address_info?.shop_name ?? "";

    temp_shipping_address.value = `${shop_name}\n${shipping_address}`;
  }
};
const onReset = () => {
  resetForm();

  resetAddressRefs();
};

const onClose = () => {
  removeQueryParams();

  const isNested = Object.hasOwn(unref(formState), "level");
  const level = unref(formState).level;

  closeForm();

  if (isNested) {
    unref(nestedRightDrawers)[level].component = null;

    unref(nestedRightDrawers)[level].props = {};
  } else {
    rightDrawerComponent.value = null;

    rightDrawerComponentProps.value = {};
  }

  toggleMethod();

  resetAddressRefs();
};

const { cancelButton } = useCancelPurchaseBill({
  purchaseBill,
  formState,
  formData,
});

watchEffect(() => {
  if (unref(formState).edit && unref(formState).open) {
    const {
      shipping_address,
      shipping_address_info,
      billing_address,
      billing_address_info,
    } = unref(purchaseBill);

    const billing_shop_name = billing_address_info?.shop_name
      ? billing_address_info.shop_name + "\n"
      : "";

    const shipping_shop_name = shipping_address_info?.shop_name
      ? shipping_address_info.shop_name + "\n"
      : "";

    temp_billing_address.value = `${billing_shop_name}${billing_address}`;

    temp_shipping_address.value = `${shipping_shop_name}${shipping_address}`;
  }
});

watchEffect(() => {
  if (!unref(formState).edit && !temp_shipping_address.value) {
    const { shipping_address, shipping_address_info } = unref(purchaseOrder);

    if (shipping_address_info?.shop_name) {
      temp_shipping_address.value = `${shipping_address_info.shop_name}\n${shipping_address}`;
    } else if (shipping_address) {
      temp_shipping_address.value = shipping_address;
    }
  }
});

watch(
  () => passedProps.source,
  async (newSource) => {
    if (newSource === "purchases") {
      const response = await getDummyBuyer();

      if (response?.data?.data) {
        const userDetails = await getUser(
          response?.data?.data || dummyBuyerDetails.value._id,
        );

        if (userDetails?.data?.data) {
          dummyBuyerDetails.value = userDetails?.data?.data;
        }
      }
    }
  },
  { immediate: true },
);
</script>
